import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import _ from '@lodash';
import { Loader } from 'app/main/apps/common/ListInfoMessage';
import { yupResolver } from '@hookform/resolvers/yup';
import BasicInfoTab from 'app/main/apps/e-commerce/product/tabs/BasicInfoTab';
import { getCategoryValues } from 'app/main/apps/settings/store/configurationsSlice';
import FuseUtils from '@fuse/utils';
import { authPermissions } from 'app/auth';
import AuraControlledAutocomplete from 'app/main/apps/common/AuraControlledAutocomplete';
import CustomProductFormContent from 'app/main/apps/e-commerce/product/dialogs/CustomProductFormContent';
import { getEcomWidgetsOrderItems } from 'app/main/dashboards/main/store/widgetsSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import { matchSorter } from 'match-sorter';
import { Box, LinearProgress, Typography } from '@mui/material';
import useSubmitOnce from 'app/main/apps/backoffice/shipments/useSubmitOnce';
import { useParams } from 'react-router-dom';
import {
  calculateWeight,
  checkNonEmptyOrNull,
  findMissingSupplierUnitCosts,
  normalizeCatalogOption,
} from 'app/main/apps/common/AuraFunctions';
import AuraBaseDialog from '../../../common/AuraBaseDialog';
import {
  getProductColors,
  getProducts,
  getProductSubTypes,
  getStatusesTypes,
  setSelectedProducts,
  getProductPolishes,
  updateProductSubTypeAndPolishes
} from '../../store/productsSlice';
import { closeProductDialog, closeViewProductDialog, saveProduct } from '../../store/productSlice';
import getInitValues, { buildProductCostPayload, getSchema, productInitValues } from '../ProductBase';
import OrderItems from '../../order/tabs/OrderItems';
import { addOrderItem, placeReOrder, updateOrder } from '../../store/orderSlice';
import { getSuppliers, quoteToOrder } from '../../store/rfqsSlice';
import SupplierOrderConfirmationDialog from '../../order/Dialogs/SupplierOrderConfirmationDialog';
import { optionKey, withoutKey } from 'app/main/apps/common/optionProps';

/**
 * @param {object} props
 * @param {'productDialog'|'viewProductDialog'} [props.slot] which store slot this instance renders.
 *   PGSS-5659/5663: the primary instance also mounts a 'viewProductDialog' instance, so View Product
 *   opened from Create Supplier Order stacks on top of it rather than replacing it.
 */
function ProductDialog({ slot = 'productDialog' }) {
  const dispatch = useDispatch();
  const productDialog = useSelector(({ eCommerceApp }) => eCommerceApp.product[slot]);
  const isViewSlot = slot === 'viewProductDialog';
  const suppliersList = useSelector((state) => state.eCommerceApp.rfqs.suppliersList);
  const selectedProducts = useSelector(({ eCommerceApp }) => eCommerceApp.products.selectedProducts);
  const unmodifiedProducts = useSelector(({ eCommerceApp }) => eCommerceApp.products.unmodifiedProducts);
  const tempSchema = getSchema();
  const [schema, setSchema] = useState(tempSchema);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const currentUser = useSelector(({ user }) => user);
  const isManage = currentUser && FuseUtils.hasPermission(authPermissions.manage, currentUser.role);
  const dialogParam = productDialog.param;
  const Param = useParams()
  const { orderId } = Param
  const [loading, setLoading] = useState(false)
  // Re Order (supplierOrder): one Create Order at a time, and say so while it runs.
  const [creatingReorder, runReorderOnce] = useSubmitOnce();
  const [showSupplierOrderConfirmationDialog, setShowSupplierOrderConfirmationDialog] = useState(false)

  const hideQty = ['Create Vas Order With Proof', 'Create Vas Order'].includes(dialogParam?.action)

  const methods = useForm({
    mode: 'onChange',
    defaultValues: getInitValues(dialogParam?.name),
    resolver: yupResolver(schema),
  });

  const { reset, formState, control, getValues, watch, setValue } = methods;
  const productType = watch('type');
  const productSubType = watch('sub_type');
  const watchProducts = watch('products');

  const { dirtyFields, isValid } = formState;
  const { fields } = useFieldArray({
    name: 'products',
    control,
    keyName: 'field_id'
  });

  const [dialogTitle, setDialogTitle] = useState('Product');
  const [dialogActionLabel, setDialogActionLabel] = useState('Create Order');

  const getCustomProductButtonLabel = () => {
    const dialogStage = dialogParam?.stage
    if (dialogStage === 'quote') return 'Add To Quote'
    if (dialogStage === 'rfq') return 'Add To RFQ'
    return 'Add To Order'
  }

  const getCustomProductEditButtonLabel = (cloneProduct) => {
    if (cloneProduct) return 'Clone'
    return 'Update'
  }

  /**
   * Initialize Dialog with Data
   */
  const initDialog = useCallback(() => {
    let dialogParamSchemaName =  dialogParam.name;
    if (['supplierOrderDirectShipment'].includes(dialogParam.name) && dialogParam.editSupplierOrder === true) {
      dialogParamSchemaName = 'supplierOrderDirectShipmentwithQty'
    }
    const currentSchema = getSchema(dialogParamSchemaName, hideQty, dialogParam?.stage, dialogParam.orderType, dialogParam.isCustomProduct);
    setSchema(currentSchema);
    if (productDialog.data) {
      const data = getInitValues(dialogParam.name, productDialog.data);
      reset({ ...data });
    }
    if (dialogParam) {
      const title = dialogParam.title || `Update ${dialogParam.name}`;
      setDialogTitle(title);
    }
    if (['supplierOrderDirectShipment'].includes(dialogParam.name)) {
      setDialogActionLabel(dialogParam.title)
    }
    if (dialogParam && (dialogParam.name === 'product' || dialogParam.name === 'custom_product' || dialogParam.name === 'edit_custom_product')) {
      if (dialogParam.name === 'custom_product') {
        const addButtonLabel = getCustomProductButtonLabel()
        setDialogActionLabel(addButtonLabel);
      }
      if (dialogParam.name === 'edit_custom_product') {
        const editButtonLabel = getCustomProductEditButtonLabel(dialogParam?.cloneProduct)
        setDialogActionLabel(editButtonLabel);
      }
      dispatch(getProductColors({}));
      dispatch(getStatusesTypes({
        params: {
          category: 'product_type',
          custom_product: true
        }
      }))
      if((isDialogType('custom_product') || isDialogType('edit_custom_product')) && productDialog?.data?.type?.id) {
        dispatch(getProductSubTypes({ path: '/api/productSubTypesByProductType', params: { id: productDialog?.data?.type?.id, custom_product: true  } }));
        dispatch(getProductPolishes({ path: '/api/polishesByProductType', params: { productTypeId: productDialog?.data?.type?.id, custom_product: true   } }));
      }
      if(!dialogParam?.cloneProduct) {
        dispatch(
            getCategoryValues({
              params: { c: 'product_category' },
            })
          )
          .unwrap()
          .then((response) => {
            const statusesTemp = response && response.data ? response.data : [];
            setCategoryTypes(statusesTemp);
          });
      }
    }
  }, [dialogParam, dispatch, productDialog.data, reset]);

  /**
   * On Dialog Open
   */
  useEffect(() => {
    if (productDialog.props.open) {
      initDialog();
    }
  }, [productDialog.props.open, initDialog]);

  const getType = () => {
    if (dialogParam.action === 'Create Vas Order') return 'vas'
    if (dialogParam?.name === 'supplierOrderDirectShipment' && dialogParam?.editSupplierOrder === true) return 'stone'
    if (['Create Stone Order', 'Create Supplier Order', 'Create Direct Shipment Supplier Order'].includes(dialogParam.action)) return 'stone'
    return 'art/design,vas'
  }

  useEffect(() => {
    if (productDialog.props.open) {
      reset()

      if (isDialogType('supplierOrder')) {
        dispatch(getSuppliers({ suppliers: [], params: { type: 'stone' } }));
      }

      if (isDialogType('supplierOrderDirectShipment')) {
        dispatch(getSuppliers({ suppliers: [], params: { type: getType() } }));
        dispatch(getEcomWidgetsOrderItems({ oid: dialogParam.orderNo, stage: dialogParam.stage }))
          .then((data) => {
            if (data && data.payload) {
              const products = data.payload?.map(d => ({
                ...d,
                stone_color: { name: d.color },
                type: { name: d.type },
                unit_cost: d.unit_cost,
                vas: { ...d.vas, textPrice: null, imagePrice: null }
              }))
              reset({ products })
            }
          });
      }
    }
  }, [productDialog.props.open]);

  /*
   * Call Product subtype and polish end point based on product type id
   * 
   */
  useEffect(() => {
    if (productType?.name) {
      let additionalParam = {custom_product: false};
      if (isDialogType('custom_product') || isDialogType('edit_custom_product')) {
        additionalParam = {custom_product: true};
      }
      dispatch(getProductSubTypes({ path: '/api/productSubTypesByProductType', params: { id: productType?.id, ...additionalParam  } }));
      dispatch(getProductPolishes({ path: '/api/polishesByProductType', params: { productTypeId: productType?.id, ...additionalParam  } }));
    } else {
      dispatch(updateProductSubTypeAndPolishes({ data: [] }))
      setValue('sub_type', '')
      setValue('polish', '')
    }
  }, [productType?.name])

  /**
   * Close Dialog
   */
  function closeDialog(type) {
    if (isViewSlot) {
      dispatch(closeViewProductDialog());
      return;
    }
    if (isDialogType('supplierOrder'))
      dispatch(setSelectedProducts({ data: unmodifiedProducts }))
    dispatch(closeProductDialog());
  }

  function isDialogType(name) {
    return dialogParam && dialogParam.name === name;
  }

  /**
   * handles create supplier order sumbit
   */
  function createSupplierOrder(data, resetConfirmPopup) {
      // PGSS-5574: VAS lines are priced at zero on purpose; inventory/custom sourcing is not.
      if (!['Create Vas Order With Proof', 'Create Vas Order'].includes(dialogParam.action)) {
        const missingSupplierCost = findMissingSupplierUnitCosts(data.products);
        if (missingSupplierCost.length > 0) {
          const names = missingSupplierCost.map((p) => p.name || p.product_name || p.id).join(', ');
          dispatch(showMessage({
            message: `Supplier cost (Unit Cost) is missing while FOB is set for: ${names}. Set Unit Cost on the product Pricing tab before creating a supplier order.`,
            type: 'error',
          }));
          return;
        }
      }
      setLoading(true)
      let payload = {}
      if (['Create Vas Order With Proof', 'Create Vas Order'].includes(dialogParam.action)) {
        payload = {
          action_name: dialogParam.action,
          supplier: { id: data?.selected_supplier?.id },
          order_details: data.products.map(p => ({ id: p.id, vas: JSON.stringify(p.vas) }))
        }
      }
      else {
        payload = {
          action_name: dialogParam.action,
          supplier: { id: data?.selected_supplier?.id },
          order_details:
            dialogParam?.editSupplierOrder === true
              ? data.products.map((p) => ({
                  id: p.id,
                  product: {
                    id: p.product_id,
                    unit_cost: p.unit_cost,
                    quantity: p.quantity,
                  },
                }))
              : data.products.map((p) => ({
                  id: p.id,
                  product: {
                    id: p.product_id,
                    unit_cost: p.unit_cost
                  },
                })),
        };
      }

      // Add this flag for if product images are required before shipment
      if (data?.is_required_product_images === true)
        payload.is_required_product_images = true;

      dispatch(quoteToOrder({ payload, id: dialogParam.orderNo }))
      .then((res) => {
        setLoading(false)
        if(res?.payload?.error) {
          dispatch(showMessage({ message: res?.payload?.message, type: 'error' }));
          dialogParam.loadProducts()
        }
        else if (!res.error) {
          // PGSS-4629: Create Supplier Inventory Order names the back-order lines it left off because they are
          // already on an open supplier inventory order; other actions send no message.
          dispatch(showMessage({ message: res?.payload?.message || 'Success', type: 'success' }));
          dialogParam.loadProducts()
        }
      });
      closeDialog();
      if(resetConfirmPopup) {
          resetSupplierOrderConfirmData();
      }
  }

  /**
   * Form Submit
   */
  function onSubmit(data) {
    // PGSS-5280: Autocomplete clear → null; type-change handlers may set ''. Normalize before any
    // productInitValues / saveProduct path so we never persist { name: '' }.
    data.sub_type = normalizeCatalogOption(data.sub_type);
    if (isDialogType('product')) {
      setLoading(true);
      dispatch(saveProduct(data)).then(() => {
        if (dialogParam.loadProducts === 'yes') {
          dispatch(getProducts());
        }
        setLoading(false);
        closeDialog()
      })
    }
    if (isDialogType('edit_custom_product') && dialogParam?.saveProductLocally === 'yes') {
      setLoading(true);
      data.isCustomProduct = 'yes';
      data.addToOrderList = 'yes';
      data.is_custom = dialogParam?.isCustomProduct
      data.cloneProduct = dialogParam?.cloneProduct
      // const param = typeof data.polish === 'string' ? data?.polish : data?.polish?.inputValue || data?.polish?.name
      data.polish = typeof data.polish === 'string' ? data?.polish : data?.polish?.inputValue || data?.polish?.name
      if (checkNonEmptyOrNull(data.weight)) {
        const { length, width, height } = data
        data.weight = calculateWeight(isManage, length, width, height)
      }
      data.is_data_editted = true;  // Added this field to notify BE that this order detail is editted
      // PGSS-5156: same FOB write mapping as the API save path — dialog unit_cost is labelled FOB.
      const costPayload = buildProductCostPayload(data, productDialog.data);
      const value = productInitValues({ ...data, ...costPayload });
      dispatch(addOrderItem(value));
      dispatch(showMessage({ message: 'Success', type: 'success' }));
      setLoading(false);
      closeDialog();
    }
    // custom_product = Copy Product
    if ((isDialogType('custom_product') || isDialogType('edit_custom_product')) && (!dialogParam.saveProductLocally  || dialogParam.saveProductLocally === 'no')) {
      setLoading(true);
      data.isCustomProduct = 'yes';
      data.addToOrderList = 'yes';
      data.is_custom = dialogParam?.isCustomProduct
      data.cloneProduct = dialogParam?.cloneProduct
      const param = typeof data.polish === 'string' ? data?.polish : data?.polish?.inputValue || data?.polish?.name
      if (checkNonEmptyOrNull(data.weight)) {
        const { length, width, height } = data
        data.weight = calculateWeight(isManage, length, width, height)
      }
      // PGSS-5156: the dialog's cost input is labelled FOB, so what the user sees and edits is the FOB —
      // send it as `fob`, not as unit_cost. The product's existing unit_cost is preserved untouched:
      // supplier order lines are priced from unit_cost (SupplierOrderUpdateService, PGSS-5006), so writing
      // the FOB into it would silently change what suppliers are paid. Converging the two columns is the
      // PGSS-5156 migration's job, done as a controlled reconciliation rather than per-save.
      // A new custom product has no prior unit_cost; the backend then falls back to the FOB (ProductRequest).
      const costPayload = buildProductCostPayload(data, productDialog.data);
      dispatch(saveProduct({ ...data, ...costPayload, polish: param })).then((res) => {
        if (dialogParam.loadProducts === 'yes') {
          dispatch(getProducts());
        }
        if (isDialogType('edit_custom_product') && !dialogParam?.cloneProduct) {
          dispatch(showMessage({ message: 'Updated successfully', type: 'success' }));
        } else if (isDialogType('custom_product') || dialogParam?.cloneProduct) {
          dispatch(showMessage({ message: 'Custom product added successfully', type: 'success' }))
        }
        setLoading(false);
        closeDialog();
      });
    }
    if (isDialogType('supplierOrder')) {
      // PGSS-5574: Unit Cost prices supplier lines; FOB is staff/customer only. Block the $0 trap.
      const missingSupplierCost = findMissingSupplierUnitCosts(data.products);
      if (missingSupplierCost.length > 0) {
        const names = missingSupplierCost.map((p) => p.name || p.product_name || p.id).join(', ');
        dispatch(showMessage({
          message: `Supplier cost (Unit Cost) is missing while FOB is set for: ${names}. Set Unit Cost on the product Pricing tab before creating a supplier order.`,
          type: 'error',
        }));
        return;
      }
      const payload = {
        supplier: { id: data?.selected_supplier?.id },
        order_details: data.products.map(product => ({ product: { id: product.id }, quantity: product.quantity, unit_cost: product.unit_cost,order_detail_ids: product.orderDetailIds }))
      }
      // Keep the dialog open, with Create Order disabled and a progress note, until the order is created.
      // It used to close straight after sending (except from the Reorder page), so a quick second click
      // could send a second supplier order. runReorderOnce drops any click while one is in flight.
      runReorderOnce(() => dispatch(placeReOrder(payload))
        .then((res) => {
          closeDialog();
          if (!res.error) {
            dispatch(showMessage({ message: 'Success', type: 'success' }));
            dialogParam.loadProducts()
          }
        }));
    }
    if (isDialogType('supplierOrderDirectShipment')) {
      if (dialogParam?.showDrawingConfirmation === true) {
        // Show confirmation popup to request for drawing or Product images are required before shipment 
        setShowSupplierOrderConfirmationDialog(true);
      } else {
        createSupplierOrder(data)
      }
    }
  }

  function handleSupplierOrderDialogSubmit() {
    setLoading(true);
    setShowSupplierOrderConfirmationDialog(false);
    const dataCopy = _.cloneDeep(getValues());
    const param = {
      id: dialogParam.orderNo,
      updateStatus: "yes",
      to_statuses_type_id: dialogParam.toStatusCode,
      supplier: { id: dataCopy?.selected_supplier?.id },
      order_details: dataCopy?.products.map((p) => ({
        id: p.id,
        product: {
          id: p.product_id,
          unit_cost: p.unit_cost,
        },
      })),
    };
    if (dataCopy?.is_drawing_required === true) {
      param.action = "request_order_drawing";
      if (dataCopy?.is_required_product_images === true)
        param.is_required_product_images = true;

      dispatch(updateOrder(param)).then((res) => {
        setLoading(false);
        if (!res.error) {
          dispatch(
            showMessage({
              message: "Submitted successfully",
              type: "success",
            })
          );
          dialogParam.loadProducts();
          closeDialog();
        } else {
          dispatch(
            showMessage({
              message: "Submission failed",
              type: "error",
            })
          );
        }
        resetSupplierOrderConfirmData();
      });
    } else {
      // If drawing is not need, follow existing flow, which is create supplier order
      // we have to call the different API If Drawing is not required 
      createSupplierOrder(dataCopy, true)
    }
  }

  function closeSupplierOrderConfirmDialog(data) {
    setShowSupplierOrderConfirmationDialog(false)
    setValue('is_drawing_required', undefined)
    setValue('is_required_product_images', undefined)
  }

  function resetSupplierOrderConfirmData() {
    setValue('is_drawing_required', undefined)
    setValue('is_required_product_images', undefined)
  }

  function disableSubmitButton() {
    if (isDialogType('supplierOrder'))
      return !isValid || loading === true || creatingReorder
    if (isDialogType('supplierOrderDirectShipment')) {
      // PGSS-4978: do NOT require a non-stock line to enable the button. This dialog backs three actions —
      // Create Supplier Order, Create Direct Shipment Supplier Order, and Create Supplier Inventory Order —
      // and the latter two (plus the RFQ/CAD Create Supplier Order flow) legitimately contain only stock
      // products. The mixed-cart non-stock sourcing is enforced by the backend filter, not by blocking the
      // button. (Previously `!hasNonStockLine` disabled the button for any all-stock order.)
      return !!(_.isEmpty(dirtyFields) || !isValid || loading === true);
    }
    return !!(_.isEmpty(dirtyFields) || !isValid || loading === true);
  }

  const isMixedCreateSupplierOrder = isDialogType('supplierOrderDirectShipment')
    && dialogParam?.action === 'Create Supplier Order'
    && (watchProducts || []).some((p) => p?.is_stock_product !== true)
    && (watchProducts || []).some((p) => p?.is_stock_product === true);

  return productDialog && productDialog.props && productDialog.props.open ? (
    <FormProvider {...methods}>
      <AuraBaseDialog
        dialogProps={productDialog.props}
        closeDialog={closeDialog}
        disableCloseIcon={loading || creatingReorder} 
        dialogParam={{
          title: dialogTitle, maxWidth: 'md',
          submitButtonName: dialogActionLabel,
          hideDialogActions: dialogParam?.hideDialogActions ? dialogParam?.hideDialogActions : false
        }}
        onSubmit={onSubmit}
        disableSubmitButton={disableSubmitButton}
        maxWidth="md"
      >
        {creatingReorder && (
          <Box sx={{ mb: 2 }} role="status" data-testid="supplier-order-in-progress">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Creating supplier order… please wait.
            </Typography>
          </Box>
        )}
        {loading ? (<Loader />) : (
         <Box component="div" sx={creatingReorder ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
        {isDialogType('product') && (
          <BasicInfoTab isManage={isManage} categoryTypes={categoryTypes} parent="order" />
        )}
        {(isDialogType('custom_product') || isDialogType('edit_custom_product')) && (
          <CustomProductFormContent
            isManage={isManage}
            categoryTypes={categoryTypes}
            parent="order"
            mode={dialogParam?.name}
            isReadOnly={dialogParam?.isReadOnly}
            customerId={dialogParam?.customerId}
            stage={dialogParam?.stage}
            orderType={dialogParam?.orderType}
            hideAdditionalFields={dialogParam?.hideAdditionalFields}
            isCustomProduct={dialogParam?.isCustomProduct}
            isInventoryProduct={dialogParam?.isInventoryProduct}
            cloneProduct={dialogParam?.cloneProduct}
            formOrderType={dialogParam?.formOrderType}
            formOrderData={dialogParam?.formOrderData}
          />
        )}
        {(isDialogType('supplierOrder') || isDialogType('supplierOrderDirectShipment')) && (
          <>
            <div className='w-1/4 mb-14'>
              <AuraControlledAutocomplete
                inputValues={suppliersList}
                control={control}
                controlName='selected_supplier'
                labelName="Select Supplier *"
                fullWidth={false}
                hideClearable
                filterOptions={(options, { inputValue }) =>
                  matchSorter(options, inputValue, {
                    keys: ['name', 'email'],
                    threshold: matchSorter.rankings.STARTS_WITH,
                  })
                }
                renderOption={(props1, option) => {
                  return <div
                    key={optionKey(props1)} {...withoutKey(props1)}
                    className='MuiAutocomplete-option !block py-2 px-10'
                  >
                    <div className="font-medium">{option.name}</div>
                    <div className="font-light text-10 text-gray-600">{option.email}</div>
                  </div>
                }}
              />
            </div>

            {/* PGSS-4978: the non-stock sourcing note + in-stock graying apply only to a MIXED Create
                Supplier Order (where in-stock items ship from inventory). They must not show for inventory
                replenishment / direct-shipment / all-stock orders, which legitimately source stock items. */}
            {isMixedCreateSupplierOrder && (
              <Typography variant="caption" className="block mb-8" sx={{ color: 'text.secondary' }}>
                Only non-stock items are sourced on a supplier order. In-stock items ship from inventory;
                shortfalls are replenished via <b>Create Supplier Inventory Order</b>.
              </Typography>
            )}

            <OrderItems fields={fields} type={dialogParam.name} hideQty={hideQty} directShipment={isDialogType('supplierOrderDirectShipment')} qtyEditable={dialogParam?.editSupplierOrder} priceEditable
              grayInStockLines={isMixedCreateSupplierOrder} />
          </>
        )}
        </Box>
        )}
      </AuraBaseDialog>
       <SupplierOrderConfirmationDialog open={showSupplierOrderConfirmationDialog} onClose={closeSupplierOrderConfirmDialog} onSubmit={handleSupplierOrderDialogSubmit} />
      {!isViewSlot && <ProductDialog slot="viewProductDialog" />}
    </FormProvider>
  ) : (
    <></>
  );
}

export default ProductDialog;